interface LogConfig {
  enabled: boolean;
  prefix: string;
  showTimestamp: boolean;
  maxInitialLogs: number;
}

class CentralizedLogger {
  private static instance: CentralizedLogger;
  private config: Record<string, LogConfig> = {};
  private logCounts: Record<string, number> = {};
  private initialPhase = true;
  private initialPhaseTimer: NodeJS.Timeout | null = null;

  private constructor() {
    // Termina la fase iniziale dopo 10 secondi
    this.initialPhaseTimer = setTimeout(() => {
      this.initialPhase = false;
      this.logOnce('system', '🚀 Piattaforma caricata completamente', 'info');
    }, 10000);
  }

  static getInstance(): CentralizedLogger {
    if (!CentralizedLogger.instance) {
      CentralizedLogger.instance = new CentralizedLogger();
    }
    return CentralizedLogger.instance;
  }

  configure(module: string, config: Partial<LogConfig>) {
    this.config[module] = {
      enabled: config.enabled ?? process.env.NODE_ENV === 'development',
      prefix: config.prefix ?? `[${module.toUpperCase()}]`,
      showTimestamp: config.showTimestamp ?? false,
      maxInitialLogs: config.maxInitialLogs ?? 3
    };
    this.logCounts[module] = 0;
  }

  private shouldLog(module: string): boolean {
    const config = this.config[module];
    if (!config || !config.enabled) return false;

    if (this.initialPhase) {
      this.logCounts[module] = (this.logCounts[module] || 0) + 1;
      return this.logCounts[module] <= config.maxInitialLogs;
    }

    return true;
  }

  log(module: string, message: string, level: 'info' | 'warn' | 'error' = 'info', data?: any) {
    if (!this.shouldLog(module)) return;

    const config = this.config[module] || { prefix: `[${module}]`, showTimestamp: false };
    const timestamp = config.showTimestamp ? ` - ${new Date().toISOString()}` : '';
    const fullMessage = `${config.prefix} ${message}${timestamp}`;

    switch (level) {
      case 'error':
        console.error(fullMessage, data || '');
        break;
      case 'warn':
        console.warn(fullMessage, data || '');
        break;
      default:
        console.log(fullMessage, data || '');
    }
  }

  logOnce(module: string, message: string, level: 'info' | 'warn' | 'error' = 'info', data?: any) {
    const key = `${module}:${message}`;
    if (!this._onceKeys) this._onceKeys = new Set();
    if (this._onceKeys.has(key)) return;
    
    this._onceKeys.add(key);
    this.log(module, message, level, data);
  }

  private _onceKeys = new Set<string>();

  // Per debugging completo quando necessario
  forceLog(module: string, message: string, level: 'info' | 'warn' | 'error' = 'info', data?: any) {
    const config = this.config[module] || { prefix: `[${module}]`, showTimestamp: false };
    const timestamp = config.showTimestamp ? ` - ${new Date().toISOString()}` : '';
    const fullMessage = `${config.prefix} ${message}${timestamp}`;

    switch (level) {
      case 'error':
        console.error(fullMessage, data || '');
        break;
      case 'warn':
        console.warn(fullMessage, data || '');
        break;
      default:
        console.log(fullMessage, data || '');
    }
  }

  getRemainingLogs(module: string): number {
    const config = this.config[module];
    if (!config) return 0;
    return Math.max(0, config.maxInitialLogs - (this.logCounts[module] || 0));
  }

  // Metodo per inizializzare tutti i moduli comuni
  initializeDefaults() {
    this.configure('notification', { maxInitialLogs: 2, prefix: '🔔' });
    this.configure('settings', { maxInitialLogs: 2, prefix: '⚙️' });
    this.configure('whatsapp', { maxInitialLogs: 3, prefix: '📱' });
    this.configure('realtime', { maxInitialLogs: 2, prefix: '🔄' });
    this.configure('layout', { maxInitialLogs: 1, prefix: '🏗️' });
    this.configure('permissions', { maxInitialLogs: 1, prefix: '🔐' });
    this.configure('system', { enabled: true, prefix: '🚀' });
    
    this.logOnce('system', '🎯 Inizializzazione piattaforma Flusia...');
  }
}

export const logger = CentralizedLogger.getInstance();

// Hook per inizializzare il logger all'avvio
export const useLoggerInit = () => {
  if (typeof window !== 'undefined') {
    logger.initializeDefaults();
  }
};